import React from 'react';
import { InspectorAvailabilityDay, User } from '@/types';
import { DAYS_OF_WEEK, TIMEZONE_OPTIONS } from './_constants';
import { getPublicOrigin } from '@/utils/booking';

// Booking Page card — slug + timezone + weekly schedule + blocked dates +
// embed code. Stateless; all data and handlers come from Settings.tsx.

interface BookingPageCardProps {
  profile: User | null;
  slug: string;
  timezone: string;
  availabilityDays: InspectorAvailabilityDay[];
  blockedDates: { blockedDate: string; reason: string | null }[];
  newBlockedDate: string;
  newBlockedReason: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setAvailabilityDays: React.Dispatch<React.SetStateAction<InspectorAvailabilityDay[]>>;
  setNewBlockedDate: (v: string) => void;
  setNewBlockedReason: (v: string) => void;
  onAddBlockedDate: () => void;
  onRemoveBlockedDate: (date: string) => void;
  slugCheckStatus: 'idle' | 'checking' | 'available' | 'taken';
  slugSuggestion: string | null;
  onSlugSuggestionAccept: (suggestion: string) => void;
}

export const BookingPageCard: React.FC<BookingPageCardProps> = ({
  profile,
  slug,
  timezone,
  availabilityDays,
  blockedDates,
  newBlockedDate,
  newBlockedReason,
  onChange,
  setAvailabilityDays,
  setNewBlockedDate,
  setNewBlockedReason,
  onAddBlockedDate,
  onRemoveBlockedDate,
  slugCheckStatus,
  slugSuggestion,
  onSlugSuggestionAccept,
}) => {
  return (
    <div className="card">
      <h2 className="text-heading-md text-ink mb-1">Booking Page</h2>
      <p className="text-body-sm text-ink-subtle mb-4">
        A public page at <code className="font-mono text-ink">/book/your-slug</code> where clients can self-schedule. Embed it on your website or share the link directly.
      </p>

      {/* Slug */}
      <div className="mb-5">
        <label className="block text-body-sm font-medium text-ink-muted mb-1">Booking slug</label>
        {/* Stack on mobile: the origin prefix is ~35 chars and pushed the
            input into a 50px sliver on iPhone portrait. Side-by-side on sm+. */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
          <span className="text-body-sm text-ink-subtle font-mono whitespace-nowrap break-all sm:break-normal">
            {getPublicOrigin()}/book/
          </span>
          <input
            type="text"
            name="slug"
            value={slug}
            onChange={onChange}
            placeholder="your-name"
            className="input-field font-mono text-body-sm min-w-0 flex-1"
          />
        </div>
        {/* Availability feedback */}
        {slugCheckStatus === 'checking' && (
          <p className="text-body-xs text-ink-subtle mt-2">Checking availability…</p>
        )}
        {slugCheckStatus === 'available' && (
          <p className="text-body-xs text-sev-maintenance-ink mt-2">✓ Available</p>
        )}
        {slugCheckStatus === 'taken' && (
          <p className="text-body-xs text-sev-major-ink mt-2">
            That slug is already taken.
            {slugSuggestion && (
              <>
                {' '}Try{' '}
                <button
                  type="button"
                  onClick={() => onSlugSuggestionAccept(slugSuggestion)}
                  className="font-mono underline"
                >
                  {slugSuggestion}
                </button>
                ?
              </>
            )}
          </p>
        )}
        {slugCheckStatus === 'idle' && (
          <p className="text-body-xs text-ink-subtle mt-2">
            Lowercase letters, numbers, and single hyphens. 3–100 characters.
          </p>
        )}
      </div>

      {/* Timezone */}
      <div className="mb-5">
        <label className="block text-body-sm font-medium text-ink-muted mb-1">Timezone</label>
        <input
          type="text"
          name="timezone"
          list="tz-options"
          value={timezone}
          onChange={onChange}
          placeholder="America/Los_Angeles"
          className="input-field font-mono text-body-sm"
        />
        <datalist id="tz-options">
          {TIMEZONE_OPTIONS.map((z) => (
            <option key={z} value={z} />
          ))}
        </datalist>
        <p className="text-body-xs text-ink-subtle mt-2">
          Your weekly hours and blocked dates are interpreted in this timezone. The booking page shows times to remote clients in this zone with a TZ label.
        </p>
      </div>

      {/* Weekly schedule */}
      <div className="mb-5">
        <p className="text-body-sm font-medium text-ink mb-2">Weekly schedule</p>
        <p className="text-body-xs text-ink-subtle mb-3">
          Toggle each day you accept bookings and set the hours. Hours are interpreted in your timezone above; the booking page renders the same wall-clock times with the TZ label.
        </p>
        <div className="space-y-2">
          {DAYS_OF_WEEK.map((dow) => {
            const day = availabilityDays.find((d) => d.dayOfWeek === dow.value)!;
            return (
              <div key={dow.value} className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 w-24 sm:w-32 shrink-0">
                  <input
                    type="checkbox"
                    checked={day.isActive}
                    onChange={(e) =>
                      setAvailabilityDays((prev) =>
                        prev.map((d) =>
                          d.dayOfWeek === dow.value ? { ...d, isActive: e.target.checked } : d
                        )
                      )
                    }
                  />
                  <span className="text-body-sm text-ink">{dow.label}</span>
                </label>
                <input
                  type="time"
                  value={day.startTime}
                  disabled={!day.isActive}
                  onChange={(e) =>
                    setAvailabilityDays((prev) =>
                      prev.map((d) =>
                        d.dayOfWeek === dow.value ? { ...d, startTime: e.target.value } : d
                      )
                    )
                  }
                  className="input-field text-body-sm flex-1 min-w-[88px] sm:flex-none sm:w-32"
                />
                <span className="text-body-sm text-ink-subtle">to</span>
                <input
                  type="time"
                  value={day.endTime}
                  disabled={!day.isActive}
                  onChange={(e) =>
                    setAvailabilityDays((prev) =>
                      prev.map((d) =>
                        d.dayOfWeek === dow.value ? { ...d, endTime: e.target.value } : d
                      )
                    )
                  }
                  className="input-field text-body-sm flex-1 min-w-[88px] sm:flex-none sm:w-32"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Blocked dates */}
      <div className="mb-5">
        <p className="text-body-sm font-medium text-ink mb-2">Blocked dates</p>
        <p className="text-body-xs text-ink-subtle mb-3">
          Days you're unavailable regardless of weekly schedule (holidays, PTO, etc.).
        </p>
        <div className="flex items-end gap-2 mb-3 flex-wrap">
          <input
            type="date"
            value={newBlockedDate}
            onChange={(e) => setNewBlockedDate(e.target.value)}
            className="input-field text-body-sm"
          />
          <input
            type="text"
            value={newBlockedReason}
            onChange={(e) => setNewBlockedReason(e.target.value)}
            placeholder="Reason (optional)"
            className="input-field text-body-sm flex-1"
          />
          <button
            type="button"
            onClick={onAddBlockedDate}
            disabled={!newBlockedDate}
            className="px-3 py-2 text-body-sm border border-line rounded-md hover:bg-surface-subtle disabled:opacity-50"
          >
            Add
          </button>
        </div>
        {blockedDates.length === 0 ? (
          <p className="text-body-xs text-ink-subtle">No blocked dates.</p>
        ) : (
          <ul className="text-body-sm">
            {blockedDates.map((b) => (
              <li key={b.blockedDate} className="flex items-center justify-between py-1 border-b border-line last:border-b-0">
                <span className="text-ink">
                  {b.blockedDate}
                  {b.reason && (
                    <span className="text-ink-subtle"> · {b.reason}</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => onRemoveBlockedDate(b.blockedDate)}
                  className="text-body-xs text-sev-major underline"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Embed code — only meaningful once a slug is saved */}
      {profile?.slug && (
        <div className="border-t border-line pt-4">
          <p className="text-body-sm font-medium text-ink mb-2">Embed on your website</p>
          <pre className="text-body-xs font-mono text-ink bg-surface-subtle p-3 rounded-md overflow-x-auto">
{`<iframe
  src="${getPublicOrigin()}/book/${profile.slug}"
  width="100%" height="700" frameborder="0"></iframe>`}
          </pre>
          <p className="text-body-xs text-ink-subtle mt-2">
            Or share the direct link:{' '}
            <a
              href={`/book/${profile.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-ink underline"
            >
              {getPublicOrigin()}/book/{profile.slug}
            </a>
          </p>
        </div>
      )}
    </div>
  );
};
